<template>
  <div id="teams" class="flex flex-col gap-6 proxima_nova">
    <div class="flex flex-row justify-between align-center">
      <h1 class="text-xl text_color font-semibold">Teams</h1>
      <a-button class="primary_medium" @click="showTeamModal"
        >Create Team</a-button
      >
    </div>

    <CustomTable
      :headers="tableHeaders"
      :data="teams"
      :pagination="pagination"
      :show-actions-column="true"
      :actions="actions"
      :custom-column-render="customColumnRender"
      @update:pagination="handlePageChange"
    />

    <CustomModal
      v-model:visible="teamModalVisible"
      :title="isEditing ? 'Edit Team' : 'Create Team'"
      width="30vw"
    >
      <div class="form_col">
        <span class="text-sm secondary_color">Team Name</span>
        <a-input
          v-model:value="teamForm.name"
          class="input_medium"
          placeholder="Enter team name"
          ref="teamNameInputRef"
          allow-clear
        />
      </div>

      <div class="form_col mt-4">
        <span class="text-sm secondary_color">Users</span>

        <a-select
          v-model:value="teamForm.user_ids"
          mode="multiple"
          :options="validUserOptions"
          :filterOption="filterOption"
        >
        </a-select>
      </div>

      <template #footer>
        <a-button class="primary_medium" @click="handleTeamModalOk">
          {{ isEditing ? "Update" : "Create" }}
        </a-button>
      </template>
    </CustomModal>
    <CustomModal
      v-model:visible="deleteModalVisible"
      title="Delete Template"
      width="32vw"
    >
      <span>Do you want to delete the team?</span>
      <template #footer>
        <a-button class="primary_medium" @click="deleteTeam">Confirm </a-button>
      </template>
    </CustomModal>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref, computed, nextTick, watch } from "vue";
import { message, Modal } from "ant-design-vue";
import { useStore } from "vuex";
import DashboardService from "@/src/api/DashboardService.js";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import CustomModal from "@/src/components/CustomModal.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import TeamMember from "./TeamMember.vue";
import { filterOption } from "../../utils/utils.js";
import { focusDelay } from "@/src/constants/commonConstants.js";

const store = useStore();

const teamNameInputRef = ref(null);
const teams = ref([]);
const allUsers = ref([]);
const teamModalVisible = ref(false);
const isEditing = ref(false);
const editingTeamId = ref(null);
const deleteModalVisible = ref(false);
const deleteTeamId = ref(null);
const teamForm = reactive({
  name: "",
  user_ids: [],
});

const validUserOptions = computed(() => {
  return allUsers.value.map((user) => ({
    label: user.name,
    value: user.id,
  }));
});

const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
});

const tableHeaders = [
  { label: "Team Name", key: "name", width: 6 },
  { label: "Members", key: "users", width: 6 },
];

const actions = [
  {
    label: "Edit",
    handler: (row) => showEditTeamModal(row),
    icon: imageNames.EDIT,
  },
  {
    label: "Delete",
    handler: (row) => showDeleteConfirm(row),
    icon: imageNames.DELETE_GREY,
  },
];

const fetchTeams = async () => {
  try {
    const response = await DashboardService.fetchTeams();
    if (response.success) {
      teams.value = response.teams.map((team) => ({
        ...team,
        users: team.users.filter((user) =>
          allUsers.value.some((validUser) => validUser.id === user.id),
        ),
      }));
      pagination.total = response.teams.length;
    }
  } catch (error) {
    console.error("Error fetching teams:", error);
    message.error("Failed to fetch teams");
  }
};

const fetchUsers = async () => {
  try {
    const response = await DashboardService.fetchUsers("active");
    if (response.success) {
      allUsers.value = response.users;
      await fetchTeams();
    }
  } catch (error) {
    console.error("Error fetching users:", error);
  }
};

const handlePageChange = (newPagination) => {
  pagination.current = newPagination.current;
  pagination.pageSize = newPagination.pageSize;
  fetchTeams();
};

const customColumnRender = {
  users: TeamMember,
};

const showTeamModal = () => {
  isEditing.value = false;
  editingTeamId.value = null;
  teamForm.name = "";
  teamForm.user_ids = [];
  teamModalVisible.value = true;
};

const showEditTeamModal = (team) => {
  isEditing.value = true;
  editingTeamId.value = team.id;
  teamForm.name = team.name;
  teamForm.user_ids = team.users
    .filter((user) =>
      allUsers.value.some((validUser) => validUser.id === user.id),
    )
    .map((user) => user.id);
  teamModalVisible.value = true;
};

const handleTeamModalOk = async () => {
  try {
    const validUserIds = teamForm.user_ids.filter((id) =>
      allUsers.value.some((user) => user.id === id),
    );

    const payload = {
      name: teamForm.name,
      user_ids: validUserIds,
    };

    let response;
    if (isEditing.value) {
      response = await DashboardService.updateTeam(
        editingTeamId.value,
        payload,
      );
    } else {
      response = await DashboardService.createTeam(payload);
    }

    if (response.success) {
      message.success(
        `Team ${isEditing.value ? "updated" : "created"} successfully`,
      );
      teamModalVisible.value = false;
      await fetchTeams();
      // Refresh teams in store for ABAC conditional permissions
      await store.dispatch("users/fetchTeams");
    } else {
      message.error(`Failed to ${isEditing.value ? "update" : "create"} team`);
    }
  } catch (error) {
    console.error("Error handling team:", error);
    message.error(
      `An error occurred while ${isEditing.value ? "updating" : "creating"} the team`,
    );
  }
};

const showDeleteConfirm = (team) => {
  deleteModalVisible.value = true;
  deleteTeamId.value = team.id;
};

const deleteTeam = async () => {
  deleteModalVisible.value = false;
  try {
    const response = await DashboardService.deleteTeam(deleteTeamId.value);
    if (response.success) {
      message.success("Team deleted successfully");
      await fetchTeams();
      // Refresh teams in store for ABAC conditional permissions
      await store.dispatch("users/fetchTeams");
    } else {
      message.error("Failed to delete team");
    }
  } catch (error) {
    console.error("Error deleting team:", error);
    message.error("An error occurred while deleting the team");
  }
};

watch(
  teamModalVisible,
  (isVisible) => {
    if (isVisible) {
      nextTick(() => {
        setTimeout(() => {
          if (teamNameInputRef?.value) {
            teamNameInputRef.value?.focus();
          }
        }, focusDelay);
      });
    }
  },
  { immediate: true },
);

onMounted(() => {
  fetchUsers();
});
</script>
